import { z } from 'zod';
import { grupoSchema, isoDateSchema, optional, versionSchema } from './common';

// ---------- Máquinas (es-pcp-maquina) ----------

export const maquinaSchema = z.object({
  maquina: z.string().min(1), // chave
  descricao: z.string(), // "Cal 5"
  grupo: grupoSchema,
  'nr-linha': z.number().int(), // 25, 47, 44, 90
  'cod-ctrab': optional(z.string()),
  'qtd-turnos': z.number().int().min(0).max(4),
  'horas-turno': z.number().min(0).max(24),
  'pct-oee-planej': z.number().min(0).max(100),
  'largura-min': optional(z.number()),
  'largura-max': optional(z.number()),
  'seq-quadro': z.number().int(),
  ativo: z.boolean(),
  version: versionSchema,
});
export type Maquina = z.infer<typeof maquinaSchema>;

/** `PUT /maquinas` (lote, cadastro fixo: sem criar nem excluir): campos parciais + chave + `version`. */
export const maquinaAtualizacaoSchema = maquinaSchema.partial().extend({
  maquina: z.string().min(1),
  version: versionSchema,
});
export type MaquinaAtualizacao = z.infer<typeof maquinaAtualizacaoSchema>;
export const maquinasLoteSchema = z.object({ maquinas: z.array(maquinaAtualizacaoSchema).min(1).max(100) });
export const maquinasQuerySchema = z.object({
  grupo: grupoSchema.optional(),
  ativo: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type MaquinasQuery = z.infer<typeof maquinasQuerySchema>;

// ---------- Turnos (es-pcp-turno) ----------

export const turnoSchema = z.object({
  turno: z.enum(['A', 'B', 'C']), // chave
  descricao: z.string(),
  'hora-ini': z.string().regex(/^\d{2}:\d{2}$/, 'Use HH:MM'), // referência, não entra no cálculo
  'hora-fim': z.string().regex(/^\d{2}:\d{2}$/, 'Use HH:MM'),
  /** duração real do turno, usada em `horas-disponiveis` (A/B = 8,33 · C = 7,33 · soma = 24 h/dia) */
  horas: z.number().gt(0).max(24),
  ativo: z.boolean(),
  version: versionSchema,
});
export type Turno = z.infer<typeof turnoSchema>;
/** `PUT /turnos` (lote, cadastro fixo): campos parciais + chave + `version`. */
export const turnoAtualizacaoSchema = turnoSchema.partial().extend({
  turno: z.enum(['A', 'B', 'C']),
  version: versionSchema,
});
export type TurnoAtualizacao = z.infer<typeof turnoAtualizacaoSchema>;
export const turnosLoteSchema = z.object({ turnos: z.array(turnoAtualizacaoSchema).min(1).max(3) });
export const turnosQuerySchema = z.object({
  ativo: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type TurnosQuery = z.infer<typeof turnosQuerySchema>;

// ---------- Faixas de capacidade (es-pcp-capac) ----------

const faixaBase = z.object({
  maquina: z.string().min(1),
  'fm-codigo': z.string(), // família; "*" = qualquer
  subfamilia: z.string(), // "*" = qualquer
  'espessura-ini': z.number().min(0),
  'espessura-fim': z.number().min(0),
  'largura-ini': z.number().min(0),
  'largura-fim': z.number().min(0),
  'peso-esp-ini': z.number().min(0),
  'peso-esp-fim': z.number().min(0),
  velocidade: z.number().min(0), // m/min
  'taxa-kgh': z.number().min(0), // 0 = calcular pela velocidade
  'setup-min': z.number().min(0),
  'pct-perda': z.number().min(0).max(100),
  'min-troca-bobina': optional(z.number().min(0)), // Slitter
  'refile-mm': optional(z.number().min(0)), // Slitter
  prioridade: z.number().int(),
  ativo: z.boolean(),
});
export const faixaCapacidadeSchema = faixaBase.extend({
  /** calculado pelo ABL para a largura e a espessura médias da faixa */
  'theoretical-kg-h-sample': optional(z.number()),
  version: versionSchema,
});
export type FaixaCapacidade = z.infer<typeof faixaCapacidadeSchema>;
/** `POST`: sem `version`. */
export const faixaCapacidadeNovaSchema = faixaBase;
export type FaixaCapacidadeNova = z.infer<typeof faixaCapacidadeNovaSchema>;

// ---------- Larguras mãe da Slitter (es-pcp-larg-mae) ----------

const larguraMaeBase = z.object({
  maquina: z.string().min(1),
  'largura-mae': z.number().positive(),
  padrao: z.boolean(),
  'refile-mm': z.number().min(0),
  'max-pistas': z.number().int().positive(),
  ativo: z.boolean(),
});
export const larguraMaeSchema = larguraMaeBase.extend({ version: versionSchema });
export type LarguraMae = z.infer<typeof larguraMaeSchema>;
export const larguraMaeNovaSchema = larguraMaeBase;
export type LarguraMaeNova = z.infer<typeof larguraMaeNovaSchema>;

// ---------- Tipos de troca (es-pcp-setup-tipo) ----------

const tipoTrocaBase = z.object({
  'tipo-troca': z.string().min(1), // "COR", "ESPESSURA", "ITEM", "LARGURA", "FACAS"
  descricao: z.string(),
  'origem-valor': z.string(), // "it-codigo", "espessura", ...
  modo: z.union([z.literal(1), z.literal(2), z.literal(3)]), // de/para | qualquer mudança | faixa numérica
  tolerancia: optional(z.number().min(0)),
  acumula: z.boolean(),
  ativo: z.boolean(),
});
export const tipoTrocaSchema = tipoTrocaBase.extend({ version: versionSchema });
export type TipoTroca = z.infer<typeof tipoTrocaSchema>;
export const tipoTrocaNovoSchema = tipoTrocaBase;
export type TipoTrocaNovo = z.infer<typeof tipoTrocaNovoSchema>;

// ---------- Matriz de troca (es-pcp-setup) ----------

const regraSetupBase = z.object({
  maquina: z.string().min(1), // "*" = todas
  'tipo-troca': z.string().min(1),
  'valor-de': z.string(), // "*" = qualquer
  'valor-para': z.string(),
  'setup-min': z.number().min(0),
  'perda-kg': z.number().min(0),
  permitido: z.boolean(),
  observacao: optional(z.string()),
});
export const regraSetupSchema = regraSetupBase.extend({ version: versionSchema });
export type RegraSetup = z.infer<typeof regraSetupSchema>;
export const regraSetupNovaSchema = regraSetupBase;
export type RegraSetupNova = z.infer<typeof regraSetupNovaSchema>;

// ---------- Calendário (es-pcp-calend) ----------

export const diaCalendarioSchema = z.object({
  maquina: z.string().min(1),
  data: isoDateSchema,
  'qtd-turnos': z.number().int().min(0).max(4),
  'horas-disponiveis': z.number().min(0).max(24),
  'horas-parada': z.number().min(0).max(24),
  'motivo-parada': optional(z.string()),
  observacao: optional(z.string()),
  version: versionSchema,
});
export type DiaCalendario = z.infer<typeof diaCalendarioSchema>;
/** `PUT /calendario` (lote): chave (`maquina` + `data`) + `version` + campos editáveis parciais. */
export const diaCalendarioAtualizacaoSchema = diaCalendarioSchema.partial().extend({
  maquina: z.string().min(1),
  data: isoDateSchema,
  version: versionSchema,
});
export type DiaCalendarioAtualizacao = z.infer<typeof diaCalendarioAtualizacaoSchema>;
export const diasLoteSchema = z.object({ dias: z.array(diaCalendarioAtualizacaoSchema).min(1).max(500) });

/** `POST /calendario/gerar`: gera os dias do intervalo, por máquina. Idempotente: dia existente não é sobrescrito. */
export const geracaoCalendarioSchema = z.object({
  maquina: z.string().min(1),
  'dt-ini': isoDateSchema,
  'dt-fim': isoDateSchema,
});
export type GeracaoCalendario = z.infer<typeof geracaoCalendarioSchema>;
export const gerarCalendarioSchema = z.object({ geracoes: z.array(geracaoCalendarioSchema).min(1).max(50) });
export const resultadoGeracaoSchema = z.object({
  situacao: z.enum(['OK', 'ERRO']),
  errornumber: z.number().int(),
  errordescription: z.string(),
  maquina: z.string(),
  'dt-ini': isoDateSchema,
  'dt-fim': isoDateSchema,
  'dias-gerados': z.number().int().nonnegative(),
  'dias-existentes': z.number().int().nonnegative(),
});
export type ResultadoGeracao = z.infer<typeof resultadoGeracaoSchema>;
export const calendarioQuerySchema = z.object({
  maquina: z.string().optional(),
  de: isoDateSchema,
  ate: isoDateSchema,
});
export type CalendarioQuery = z.infer<typeof calendarioQuerySchema>;

// ---------- Escritas em lote: `{ "<entidade>": [...] }` ----------

/**
 * Entidades que gravam em lote (`POST`/`PUT`/`DELETE`). O corpo é `{ "<chave>": [...] }`, SEM `payload` externo:
 * o framework REST do Datasul já envelopa o corpo sob `payload` antes de entregar ao ABL.
 */
export const ENTIDADES_LOTE = ['capacidade', 'largura-mae', 'tipo-troca', 'setup'] as const;
export type EntidadeLote = (typeof ENTIDADES_LOTE)[number];
export type MetodoLote = 'POST' | 'PUT' | 'DELETE';

/**
 * Chave do array no corpo de cada entidade. `capacidade` e `larguras-mae` estão no contrato; `tipos-troca` e
 * `setup` seguem o mesmo padrão (plural do nome da rota) e serão conferidos com os `reference/*.p`.
 */
export const CHAVE_LOTE: Record<EntidadeLote, string> = {
  capacidade: 'capacidade',
  'largura-mae': 'larguras-mae',
  'tipo-troca': 'tipos-troca',
  setup: 'setup',
};

/** Schema do corpo para cada entidade e método: `POST` não leva `version`; `PUT`/`DELETE` levam. */
export const loteBodySchema = (entidade: EntidadeLote, metodo: MetodoLote) => {
  const novo = {
    capacidade: faixaCapacidadeNovaSchema,
    'largura-mae': larguraMaeNovaSchema,
    'tipo-troca': tipoTrocaNovoSchema,
    setup: regraSetupNovaSchema,
  }[entidade];
  const existente = {
    capacidade: faixaCapacidadeSchema,
    'largura-mae': larguraMaeSchema,
    'tipo-troca': tipoTrocaSchema,
    setup: regraSetupSchema,
  }[entidade];
  const linha = metodo === 'POST' ? novo : existente;
  return z.object({ [CHAVE_LOTE[entidade]]: z.array(linha).min(1).max(500) });
};

export const capacidadeQuerySchema = z.object({
  maquina: z.string().optional(),
  'fm-codigo': z.string().optional(),
});

export const larguraMaeQuerySchema = z.object({ maquina: z.string().optional() });
export type LarguraMaeQuery = z.infer<typeof larguraMaeQuerySchema>;
export type CapacidadeQuery = z.infer<typeof capacidadeQuerySchema>;
